/** OTP verify setup token — session-scoped, cleared when the tab closes or signup completes. */
export const SIGNUP_SETUP_SESSION_KEY = 'launchpad_signup_setup'

export type SignupSetupSession = {
  email: string
  setupToken: string
}

export function saveSignupSetupToSession(email: string, setupToken: string): void {
  if (typeof window === 'undefined') return
  try {
    const payload: SignupSetupSession = { email: email.trim().toLowerCase(), setupToken }
    sessionStorage.setItem(SIGNUP_SETUP_SESSION_KEY, JSON.stringify(payload))
  } catch {
    // private / quota — onboarding still works from in-memory state
  }
}

export function readSignupSetupFromSession(): SignupSetupSession | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = sessionStorage.getItem(SIGNUP_SETUP_SESSION_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as unknown
    if (!parsed || typeof parsed !== 'object') return null
    const email = (parsed as { email?: unknown }).email
    const setupToken = (parsed as { setupToken?: unknown }).setupToken
    if (typeof email !== 'string' || typeof setupToken !== 'string') return null
    if (!setupToken.trim()) return null
    return { email: email.trim().toLowerCase(), setupToken }
  } catch {
    return null
  }
}

export function clearSignupSetupSession(): void {
  if (typeof window === 'undefined') return
  try {
    sessionStorage.removeItem(SIGNUP_SETUP_SESSION_KEY)
  } catch {
    /* ignore */
  }
}

/** Resolve setup token for signup: explicit state first, then session for same email. */
export function resolveSignupSetupToken(email: string, fromState: string | null): string | null {
  const trimmed = fromState?.trim() ?? ''
  if (trimmed) return trimmed
  const session = readSignupSetupFromSession()
  if (!session) return null
  if (session.email !== email.trim().toLowerCase()) return null
  return session.setupToken
}

/* ─── Password-reset session (same pattern as signup) ─── */

const RESET_SESSION_KEY = 'launchpad_password_reset'

export type PasswordResetSession = {
  email: string
  resetToken: string
}

export function savePasswordResetToSession(email: string, resetToken: string): void {
  if (typeof window === 'undefined') return
  try {
    const payload: PasswordResetSession = { email: email.trim().toLowerCase(), resetToken }
    sessionStorage.setItem(RESET_SESSION_KEY, JSON.stringify(payload))
  } catch {
    /* private / quota — in-memory state still works */
  }
}

export function readPasswordResetFromSession(): PasswordResetSession | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = sessionStorage.getItem(RESET_SESSION_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as unknown
    if (!parsed || typeof parsed !== 'object') return null
    const email = (parsed as { email?: unknown }).email
    const resetToken = (parsed as { resetToken?: unknown }).resetToken
    if (typeof email !== 'string' || typeof resetToken !== 'string') return null
    if (!resetToken.trim()) return null
    return { email: email.trim().toLowerCase(), resetToken }
  } catch {
    return null
  }
}

export function clearPasswordResetSession(): void {
  if (typeof window === 'undefined') return
  try {
    sessionStorage.removeItem(RESET_SESSION_KEY)
  } catch {
    /* ignore */
  }
}

export function resolveResetToken(email: string, fromState: string | null): string | null {
  const trimmed = fromState?.trim() ?? ''
  if (trimmed) return trimmed
  const session = readPasswordResetFromSession()
  if (!session) return null
  if (session.email !== email.trim().toLowerCase()) return null
  return session.resetToken
}
